<template>
  <v-container fluid>
    <PageHeaderSlot showBackButton />

    <v-form ref="dataForm" @submit.prevent="">
      <v-card>
        <v-card-text class="px-2">
          <v-row class="ma-0">
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormUploader
                uploadText="uploadCover"
                fileInputKey="upload_thumbnail"
                :files.sync="formData.thumbnail_list"
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                single
              />
            </v-col>
            <v-col cols="12">
              <FormInput
                label="title"
                placeholder="title"
                :fieldValue.sync="formData.title"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
              />
            </v-col>
            <v-col cols="12">
              <QuillEditor :html.sync="formData.content" editorID="content" label="content" />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormSelect
                label="category"
                placeholder="selectCategory"
                :fieldValue.sync="formData.category"
                :options="categoryOptions"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormDatePicker
                label="publishDate"
                placeholder="publishDate"
                :dateValue.sync="formData.publish_date"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
                disabledActionButtons
                disableClear
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormTimePicker
                label="publishTime"
                placeholder="publishTime"
                :fieldValue.sync="formData.publish_time"
                :options="timeOptions"
                required
                :dispatchUpdateOnChange="dispatchUpdateOnChange"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <div class="d-flex align-center mt-4">
        <FormButton @click="save()" />
        <FormButton
          v-if="$validate.DataValid(id)"
          class="ml-4"
          outlined
          color="error"
          text="delete"
          @click="handleDelete()"
        />
      </div>
    </v-form>
  </v-container>
</template>

<script>
import FormInput from '@/components/formField/FormInput.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import FormTimePicker from '@/components/formField/FormTimePicker.vue'
import QuillEditor from '@/components/formField/QuillEditor.vue'
import FormUploader from '@/components/formField/FormUploader.vue'

export default {
  name: 'BlogDetails',
  inject: ['getConfirmDialogRef'],
  components: {
    FormInput,
    FormSelect,
    QuillEditor,
    FormTimePicker,
    FormDatePicker,
    FormUploader,
  },
  computed: {
    id() {
      return this.$validate.regexNumber(this.$route.params.id) ? parseInt(this.$route.params.id) : null
    },
    dispatchUpdateOnChange() {
      return this.$validate.DataValid(this.id)
    },
  },
  data: () => ({
    formData: {
      category: null,
      thumbnail_list: [],
      title: '',
      publish_date: '',
      publish_time: '',
      content: '',
    },

    categoryOptions: [],
    timeOptions: [],
  }),
  methods: {
    async _getBlogById() {
      try {
        const data = await this.$Fetcher.GetBlogById(this.id)
        for (const key in this.formData) {
          if (this.$validate.DataValid(data[key])) {
            this.$set(this.formData, key, data[key])
          } else if (key === 'thumbnail_list' && this.$validate.DataValid(data['thumbnail'])) {
            this.formData[key] = []
            this.formData[key].push({ name: data['thumbnail'], file: data['thumbnail'] })
          }
        }
      } catch (err) {
        this.$common.error(err)
        if (err !== 'abort') {
          this.showAlert({
            message: 'noData',
            returnLink: 'BlogList',
          })
        }
      }
    },
    async _getCategoryOptions() {
      try {
        const { data } = await this.$Fetcher.GetBlogCategories()
        this.categoryOptions = data.map(el => {
          return { value: el.id, text: el.name }
        })
      } catch {}
    },

    // -- (確認)
    async save() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const valid = this.$refs['dataForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', { message: 'required' })
        return
      }

      this.$store.dispatch('setLoading', true)

      try {
        const payload = {
          category: this.formData.category,
          thumbnail: this.formData.thumbnail_list.length ? this.formData.thumbnail_list[0].file : '',
          title: this.formData.title,
          publish: `${this.formData.publish_date}T${this.formData.publish_time}:00Z`,
          content: this.formData.content,
        }

        let message = {}
        if (this.$validate.DataValid(this.id)) {
          await this.$Fetcher.UpdateBlog(this.id, payload)
          message = {
            message: 'message.saveSuccess',
            refresh: true,
          }
        } else {
          await this.$Fetcher.NewBlog(payload)
          message = {
            message: 'message.createSuccess',
            redirect: 'BlogList',
          }
        }
        this.$store.dispatch('setDataIsUpdated', false)
        this.$store.dispatch('toggleSnack', {
          ...message,
          type: 'success',
        })
      } catch (err) {
        this.$common.error(err)
        this.showAlert({
          title: this.$validate.DataValid(this.id) ? 'message.saveFail' : 'message.createFail',
          message: err,
          isError: true,
        })
      } finally {
        this.$store.dispatch('setLoading', false)
      }
    },

    // -- (刪除)
    async handleDelete() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const confirmRef = this.getConfirmDialogRef()
      const confirm = await confirmRef.show({ isDelete: true })
      if (confirm) {
        this.$store.dispatch('setLoading', true)

        try {
          await this.$Fetcher.DeleteBlog(this.id)
          this.$store.dispatch('toggleSnack', {
            message: 'message.deleteSuccess',
            type: 'success',
            redirect: 'BlogList',
          })
        } catch (err) {
          this.$common.error(err)
          this.showAlert({
            title: 'message.deleteFail',
            message: err,
            isError: true,
          })
        } finally {
          this.$store.dispatch('setLoading', false)
        }
      }
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: BlogDetails')
      await this._getCategoryOptions()
      if (this.$validate.DataValid(this.id)) {
        await this._getBlogById()
      }
      this.$store.dispatch('toggleLoadingPage', false)
    },
  },

  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  created() {
    this.timeOptions = []
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j++) {
        this.timeOptions.push(`${this.$formatter.pad(i)}:${this.$formatter.pad(j)}`)
      }
    }
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters.isLoading) {
      this.$store.dispatch('toggleSnack', { message: 'processing' })
      next(false)
    } else if (this.$store.getters.dataIsUpdated) {
      this.$store.dispatch('showLeaveAlert', { toLink: { name: to.name } })
      next(false)
    } else {
      next()
    }
  },
}
</script>
